import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getBrowserHash } from "../utils/urlHash";
import type { ChatScopeFilters } from "../api";
import type { AskSuggestion } from "./askStarters";
import type { DateRangePreset } from "../components/dashboard/dateSelection";
import { readChatDraft, setChatDraft } from "./chatDraft";
import { chatSessionSnapshot } from "./chatStore";
import { questionChatKey, recentQuestionsSnapshot } from "../watchlist/recentQuestions";
import type { ScopeTouched, WatchlistScope } from "../watchlist/types";

export type ContextualAsk = {
  page?: {
    kind: "overview" | "processes" | "process" | "team" | "session" | "kpis";
    name?: string;
    insight?: string;
  };
  /** Page evidence, such as saved KPI results, supplied with the question. */
  context?: string;
  sessionKey: string;
  label: string;
  filters: ChatScopeFilters;
  suggestions: (string | AskSuggestion)[];
  contextItems?: { id: string; name: string; kind: "step" | "instance" }[];
  empty?: boolean;
  customScope?: WatchlistScope;
  baseInstanceIds?: string[] | null;
  sessionProcesses?: string[];
  sessionLabel?: string;
  sessionDetails?: string;
  datePreset?: DateRangePreset;
  /** Dates describe the selected runs; the API accepts exact IDs without date bounds. */
  runSelection?: {
    from: string;
    to: string;
    selectedIds?: string[];
    runs: { id: string; label: string; userId: string; from: string; to: string }[];
  };
  result?: { id: string; label: string; context: string; scopeKnown: boolean };
};
export type QuestionSelection = {
  questionId?: string;
  scope?: WatchlistScope;
  scopeTouched?: ScopeTouched;
  context?: ContextualAsk;
};
export type WatchRequest = {
  context?: ContextualAsk | null;
  scope?: WatchlistScope;
  prompt?: string;
  processSlug?: string;
  fromConversation?: boolean;
};
const newId = (): string =>
  window.crypto.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const initialState = {
  open: false,
  initialized: false,
  history: false,
  followingPage: true,
  conversationId: "",
  target: null as ContextualAsk | null,
  question: {} as QuestionSelection,
  watchRequest: null as (WatchRequest & { id: string }) | null,
};
const noop = () => {};

/** Whether a chat has anything of its own yet: a message, a draft, or a KPI being set up. */
const chatStarted = (state: typeof initialState): boolean => {
  const key = questionChatKey(state.conversationId);
  return Boolean(
    state.watchRequest || readChatDraft(key).trim() || chatSessionSnapshot(key).entries.length,
  );
};

/** A page's context, keeping the result the chat was opened to ask about. */
const withResult = (page: ContextualAsk | null, target: ContextualAsk | null) =>
  page && target?.result ? { ...page, result: target.result } : page;

export const getAskShortcut = () => {
  const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return {
    label: mac ? "⌘J" : "Ctrl+J",
    ariaKeyShortcuts: mac ? "Meta+J" : "Control+J",
    modifier: mac ? ("metaKey" as const) : ("ctrlKey" as const),
  };
};

const AskContext = createContext({
  ...initialState,
  expanded: false,
  docked: false,
  toggleDocked: noop,
  page: null as ContextualAsk | null,
  toggle: noop,
  setHistory: (_history: boolean) => {},
  close: noop,
  toggleExpanded: noop,
  followPage: noop,
  setContext: (_target: ContextualAsk) => {},
  openContext: (_target: ContextualAsk, _prompt?: string) => {},
  openQuestion: (_question: QuestionSelection) => {},
  openWatch: (_request?: WatchRequest) => {},
  finishWatch: noop,
  removeContext: (_id: string) => {},
  registerPage: (_page: ContextualAsk | null) => {},
});

export function AskProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const expanded = /^\/chat(?:\/|$)/.test(location.pathname);
  const returnTo = useRef("/");
  const [state, setState] = useState(() => ({ ...initialState, conversationId: newId() }));
  const [docked, setDocked] = useState(false);
  const toggleDocked = useCallback(() => setDocked(value => !value), []);
  const setHistory = useCallback(
    (history: boolean) => setState(current => ({ ...current, history })),
    [],
  );
  const [page, setPage] = useState<ContextualAsk | null>(null);
  const registerPage = useCallback((next: ContextualAsk | null) => {
    setPage(next);
    setState(current => {
      if (current.followingPage) return { ...current, target: next };
      // Context set on one page belongs to a chat about it. With nothing asked or typed yet
      // there is no such chat, so arriving on another page resumes following it.
      if (next && askSourcePage(current.target, next) && !chatStarted(current))
        return { ...current, followingPage: true, target: withResult(next, current.target) };
      return current;
    });
  }, []);
  const close = useCallback(() => {
    setDocked(false);
    setState(current => ({ ...current, open: false }));
    if (expanded) navigate(returnTo.current);
  }, [expanded, navigate]);
  const toggleExpanded = useCallback(() => {
    if (expanded) navigate(returnTo.current);
    else {
      returnTo.current = location.pathname + location.search + getBrowserHash();
      setState(current => ({ ...current, followingPage: false }));
      navigate(`/chat/${state.conversationId}/`);
    }
  }, [expanded, location, navigate, state.conversationId]);
  const followPage = useCallback(
    () =>
      setState(current => ({
        ...current,
        followingPage: true,
        target: withResult(page, current.target),
      })),
    [page],
  );
  const setContext = useCallback(
    (target: ContextualAsk) => setState(current => ({ ...current, target, followingPage: false })),
    [],
  );
  const openContext = useCallback(
    (target: ContextualAsk, prompt?: string) => {
      if (prompt !== undefined) setChatDraft(questionChatKey(state.conversationId), prompt);
      setState(current => ({
        ...current,
        target,
        open: true,
        initialized: true,
        followingPage: false,
        watchRequest: null,
      }));
    },
    [state.conversationId],
  );
  const openQuestion = useCallback(
    (question: QuestionSelection) => {
      const recent = recentQuestionsSnapshot().find(item => item.id === question.questionId);
      const conversationId = question.questionId ?? newId();
      if (expanded && location.pathname !== `/chat/${conversationId}/`)
        navigate(`/chat/${conversationId}/`, { replace: /^\/chat\/?$/.test(location.pathname) });
      setState(current => ({
        ...current,
        open: true,
        initialized: true,
        conversationId,
        history: false,
        followingPage: !question.questionId && !question.context && !expanded,
        target: question.questionId
          ? (question.context ?? recent?.context ?? null)
          : (question.context ?? (expanded ? null : page)),
        question: {
          ...recent,
          ...question,
          scope: question.scope ?? recent?.scope,
        },
        watchRequest: null,
      }));
    },
    [page, expanded, navigate, location.pathname],
  );
  const openWatch = useCallback(
    (request: WatchRequest = {}) => {
      setState(current => ({
        ...current,
        open: true,
        initialized: true,
        history: false,
        watchRequest: { context: current.target ?? page, ...request, id: newId() },
      }));
    },
    [page],
  );
  const finishWatch = useCallback(
    () => setState(current => ({ ...current, watchRequest: null })),
    [],
  );
  const removeContext = useCallback((id: string) => {
    setState(current => {
      if (!current.target) return current;
      const target = current.target;
      const contextItems = target.contextItems?.filter(item => item.id !== id);
      return {
        ...current,
        target: {
          ...target,
          contextItems,
          filters: {
            ...target.filters,
            selectedSteps: contextItems
              ?.filter(item => item.kind === "step")
              .map(item => item.name),
            instanceIds: contextItems?.some(item => item.kind === "instance")
              ? contextItems.filter(item => item.kind === "instance").map(item => item.id)
              : target.baseInstanceIds,
          },
        },
      };
    });
  }, []);
  const toggle = useCallback(() => {
    if (state.open) close();
    else setState(current => ({ ...current, open: true, initialized: true }));
  }, [state.open, close]);
  const focusBeforeShortcut = useRef<HTMLElement | null>(null);
  const restoreFocus = useRef(false);
  useEffect(() => {
    const { modifier } = getAskShortcut();
    const onKeyDown = (event: KeyboardEvent) => {
      // Let nested menus/dialogs consume Escape before closing the panel.
      if (
        event.key === "Escape" &&
        state.open &&
        !event.defaultPrevented &&
        !event.isComposing &&
        !event.repeat &&
        !document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]',
        )
      ) {
        event.preventDefault();
        restoreFocus.current = true;
        close();
        return;
      }
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.key.toLowerCase() !== "j" ||
        !event[modifier] ||
        event[modifier === "metaKey" ? "ctrlKey" : "metaKey"] ||
        event.altKey ||
        event.shiftKey
      )
        return;
      // Suppress the browser shortcut even while the key is held down.
      event.preventDefault();
      if (event.repeat) return;
      if (state.open) {
        restoreFocus.current = true;
        close();
      } else {
        focusBeforeShortcut.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setState(current => ({ ...current, open: true, initialized: true, history: false }));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [state.open, close]);
  useEffect(() => {
    if (state.open) return;
    if (restoreFocus.current) {
      restoreFocus.current = false;
      const previous = focusBeforeShortcut.current;
      const target =
        previous?.isConnected && previous !== document.body
          ? previous
          : document.querySelector<HTMLElement>('[aria-controls="ask-sidebar"]');
      target?.focus({ preventScroll: true });
    }
    focusBeforeShortcut.current = null;
  }, [state.open]);
  const value = useMemo(
    () => ({
      ...state,
      expanded,
      docked,
      toggleDocked,
      page,
      toggle,
      setHistory,
      close,
      toggleExpanded,
      followPage,
      setContext,
      openContext,
      openQuestion,
      openWatch,
      finishWatch,
      removeContext,
      registerPage,
    }),
    [
      state,
      expanded,
      docked,
      toggleDocked,
      page,
      toggle,
      setHistory,
      close,
      toggleExpanded,
      followPage,
      setContext,
      openContext,
      openQuestion,
      openWatch,
      finishWatch,
      removeContext,
      registerPage,
    ],
  );
  return <AskContext.Provider value={value}>{children}</AskContext.Provider>;
}
export const useAskSidebar = () => useContext(AskContext);

/**
 * The page a chat takes its context from, named only when it is not the page on screen: after the
 * scope was edited, a past chat reopened, or Ask expanded over the page.
 */
export function askSourcePage(target: ContextualAsk | null, page: ContextualAsk | null) {
  const source = target?.page;
  const current = page?.page;
  return source && !(current?.kind === source.kind && current.name === source.name)
    ? source
    : undefined;
}

/** Page filters follow navigation until the user explicitly overrides chat context. */
export function usePageAsk(page: ContextualAsk | null) {
  const { registerPage } = useAskSidebar();
  // Pages that memoise `page` skip re-serialising it on every render.
  const serialized = useMemo(() => JSON.stringify(page), [page]);
  useEffect(() => {
    registerPage(JSON.parse(serialized));
    return () => registerPage(null);
  }, [registerPage, serialized]);
}
